import { useEffect, useRef, useState } from 'react';
import { copyText } from '../../lib/clipboard';
import { showToast } from '../Toaster';
import { quietSmButtonClass } from './AdminUi';

const COPIED_MS = 2_000;
const MANUAL_MESSAGE = 'We could not reach the clipboard. Select the text and press ⌘C.';

type CopyState = 'idle' | 'copied' | 'manual';

/**
 * One copy control for every admin copy site: it confirms inline rather than
 * only in a toast, and when the browser refuses the clipboard it shows the
 * text selected, ready for ⌘C, instead of failing with an apology.
 */
export default function CopyButton({
  text,
  onResolveText,
  label = 'Copy link',
  copiedLabel = 'Copied ✓',
  busyLabel = 'Copying',
  manualLabel = 'Link to copy manually',
  successMessage,
  className = quietSmButtonClass,
  disabled = false,
  onManualFallback,
}: {
  /** Text that already exists, so the clipboard call runs inside the click. */
  text?: string | null;
  /** Fetches text that does not exist yet, such as an edit access link on first copy. */
  onResolveText?: () => Promise<string | null>;
  label?: string;
  copiedLabel?: string;
  busyLabel?: string;
  manualLabel?: string;
  successMessage?: string;
  className?: string;
  disabled?: boolean;
  /** Provide when the page already shows the text, and select it there instead. */
  onManualFallback?: (text: string) => void;
}) {
  const [state, setState] = useState<CopyState>('idle');
  const [manualText, setManualText] = useState('');
  const [busy, setBusy] = useState(false);
  const resetTimer = useRef<number | null>(null);
  const manualField = useRef<HTMLInputElement>(null);
  const resolved = useRef<string | null>(null);
  const resolving = useRef<Promise<string | null> | null>(null);

  useEffect(() => () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
  }, []);

  useEffect(() => {
    if (state === 'manual') manualField.current?.select();
  }, [state]);

  // The pre-resolved value only ever stands in for an absent `text`, so any
  // change to `text` retires it. Without this, revoking a link left the minted
  // URL cached and the next click copied a dead link with nothing to show for it.
  useEffect(() => {
    resolved.current = null;
    resolving.current = null;
  }, [text]);

  /**
   * Resolves the text before the click, on hover, so the clipboard call runs
   * inside the gesture that triggered it. Awaiting a worker round trip mid-click
   * spends the user activation that Safari's Clipboard API and the execCommand
   * selection fallback both require — the exact path the embedded SS OS iframe
   * depends on, where the Clipboard API is already denied by permissions policy.
   *
   * Hover only, never focus: resolving mints a live edit-access credential, and
   * tabbing past a button is not a request for one.
   */
  function prepare() {
    if (disabled || busy || text || resolved.current || resolving.current || !onResolveText) return;
    const pending = onResolveText()
      .then((value) => {
        resolved.current = value;
        return value;
      })
      .catch(() => null)
      .finally(() => {
        if (resolving.current === pending) resolving.current = null;
      });
    resolving.current = pending;
  }

  async function copy() {
    let value = text ?? resolved.current;
    if (!value && onResolveText) {
      setBusy(true);
      try {
        value = await (resolving.current ?? onResolveText());
        resolved.current = value;
      } catch {
        // The caller surfaces its own failure message.
        value = null;
      } finally {
        setBusy(false);
      }
    }
    if (!value) return;
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    const outcome = await copyText(value);
    if (outcome === 'failed') {
      showToast('error', MANUAL_MESSAGE);
      if (onManualFallback) {
        onManualFallback(value);
        return;
      }
      setManualText(value);
      setState('manual');
      return;
    }
    setState('copied');
    if (successMessage) showToast('success', successMessage);
    resetTimer.current = window.setTimeout(() => setState('idle'), COPIED_MS);
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => void copy()}
        onPointerEnter={prepare}
        disabled={disabled || busy}
        aria-live="polite"
      >
        {busy ? (
          <>
            <span className="inline-spinner" aria-hidden="true" />
            {busyLabel}
          </>
        ) : state === 'copied' ? copiedLabel : label}
      </button>
      {state === 'manual' ? (
        <div className="mt-2 flex w-full flex-wrap items-center gap-2">
          <input
            ref={manualField}
            className="min-h-9 w-full min-w-40 flex-1 rounded-input border border-line bg-card px-2 py-1 font-mono text-[11px] text-fg"
            value={manualText}
            readOnly
            aria-label={manualLabel}
            onFocus={(event) => event.currentTarget.select()}
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">Press ⌘C</span>
        </div>
      ) : null}
    </>
  );
}
